import { ReactElement } from 'react';

import {
   DayOfWeek,
   TimeOfDay,
   WaitlistEntryCreateCommand,
   WaitlistEntryQuery,
} from '@dreryk/egabinet-waitlist-frontend-api';
import { PatientId } from '@dreryk/egabinet-patient-published-language-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';

import {
   DataSyncWaitingEventInterceptor,
   dispatchSnackbarPositiveEvent,
   ModalConfirmButton,
} from '@dreryk/react-components';
import { refetchCqrsQuery, useCqrsCommand, useDataSyncing } from '@dreryk/command-query-react-common';
import { Choice } from '@dreryk/watermelon';
import { FormContextValues } from '@dreryk/react-common';

import { translationKeys } from '../translations';
import { isPractitionerView } from '../form/fields/PractitionerField/PractitionerView.utils';

import { WaitlistEntryCreateFormBean } from './WaitlistEntryCreate.types';

type PatientAvailability = { [D in DayOfWeek]?: TimeOfDay[] };

type WaitlistEntryCreationModalConfirmButtonProps = {
   handleSubmit: FormContextValues<WaitlistEntryCreateFormBean>['handleSubmit'];
   onSuccess: VoidFunction;
};

export const WaitlistEntryCreateModalConfirmButton = (
   props: WaitlistEntryCreationModalConfirmButtonProps
): ReactElement => {
   const { executeAsync: createWaitlistEntry } = useCqrsCommand<void, WaitlistEntryCreateFormBean>(
      (parameters) => {
         const dayTimePreferences = Object.keys(parameters.dayTimePreferences).reduce<PatientAvailability>(
            (acc, dayOfWeek) => {
               const currentPreference = parameters.dayTimePreferences[dayOfWeek as DayOfWeek];
               if (currentPreference.length) {
                  acc[dayOfWeek as DayOfWeek] = currentPreference;
               }
               return acc;
            },
            {}
         );

         const command = new WaitlistEntryCreateCommand();
         command.patientId = PatientId.of(parameters.patient!.id);
         command.practitionerId =
            parameters.practitioner && isPractitionerView(parameters.practitioner)
               ? PractitionerId.of(parameters.practitioner.id)
               : parameters.practitioner!.id;
         command.importance = parameters.urgent ? 'IMPORTANT' : 'REGULAR';
         command.note = Choice.withUseDefaultIfNull(parameters.note);
         command.reservationPreference = parameters.reservationPreference;
         command.locationId = Choice.withUseDefaultIfNull(parameters.location?.id ?? null);
         command.encounterMode = parameters.videoConsultation ? 'VIDEO_CONSULTATION' : 'ON_SITE';
         command.receptionServiceId = Choice.withUseDefaultIfNull(parameters.service ? parameters.service.id : null);
         command.patientAvailability = Choice.withUseDefaultIfNull(
            Object.keys(dayTimePreferences).length ? dayTimePreferences : null
         );
         return command;
      },
      {
         onSuccess: () => {
            props.onSuccess();
            dispatchSnackbarPositiveEvent(translationKeys.entryCreatedSuccessfully);
            refetchCqrsQuery(WaitlistEntryQuery);
         },
      }
   );

   return (
      <DataSyncWaitingEventInterceptor waitUntil={useDataSyncing()}>
         {(waiting) => (
            <ModalConfirmButton
               label={translationKeys.save}
               onClick={props.handleSubmit(createWaitlistEntry)}
               disabled={waiting}
            />
         )}
      </DataSyncWaitingEventInterceptor>
   );
};
